import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import Web3Status from 'components/Web3Status'
import { V2_FACTORY_ADDRESSES } from 'constants/addresses'
import { chainIdToBackendName } from 'graphql/data/util'
import { useIsFarmsPage, useIsNftPage } from 'hooks/useIsNftPage'
import { useIsPoolsPage } from 'hooks/useIsPoolsPage'
import { useAtomValue } from 'jotai/utils'
import { Box } from 'nft/components/Box'
import { Row } from 'nft/components/Flex'
import { useProfilePageState } from 'nft/hooks'
import { ProfilePageStateType } from 'nft/types'
import { ReactNode } from 'react'
import { NavLink, NavLinkProps, useLocation, useNavigate } from 'react-router-dom'
import { shouldDisableNFTRoutesAtom } from 'state/application/atoms'
import styled from 'styled-components/macro'
import { useIsDarkMode } from 'theme/components/ThemeToggle'

import { Bag } from './Bag'
import Blur from './Blur'
import { ChainSelector } from './ChainSelector'
import { MenuDropdown } from './MenuDropdown'
import * as styles from './style.css'
import { isAstarEVMChain } from 'constants/chains'

const Nav = styled.nav`
  padding: 20px 12px;
  width: 100%;
  height: ${({ theme }) => theme.navHeight}px;
  z-index: 2;
`

interface MenuItemProps {
  href: string
  id?: NavLinkProps['id']
  isActive?: boolean
  children: ReactNode
  dataTestId?: string
}

const MenuItem = ({ href, dataTestId, id, isActive, children }: MenuItemProps) => {
  return (
    <NavLink
      to={href}
      className={isActive ? styles.activeMenuItem : styles.menuItem}
      id={id}
      style={{ textDecoration: 'none' }}
      data-testid={dataTestId}
    >
      {children}
    </NavLink>
  )
}

export const PageTabs = () => {
  const { pathname } = useLocation()
  const { chainId: connectedChainId } = useWeb3React()

  const isPoolActive = useIsPoolsPage()
  const isNftPage = useIsNftPage()
  const isFarmsPage = useIsFarmsPage()

  const shouldDisableNFTRoutes = useAtomValue(shouldDisableNFTRoutesAtom)
  const showV2Features = Boolean(connectedChainId && V2_FACTORY_ADDRESSES[connectedChainId])

  return (
    <>
      <MenuItem href="/swap" isActive={pathname.startsWith('/swap')}>
        <Trans>Swap</Trans>
      </MenuItem>
      {/*<MenuItem href={`/tokens/${chainName.toLowerCase()}`} isActive={pathname.startsWith('/tokens')}>
        <Trans>Tokens</Trans>
      </MenuItem>
      /*/}
      {!shouldDisableNFTRoutes && (
        <MenuItem dataTestId="nft-nav" href="/nfts" isActive={isNftPage}>
          <Trans>NFTs</Trans>
        </MenuItem>
      )}
      <Box display={{ sm: 'flex', lg: 'none', xxl: 'flex' }} width="full">
        <MenuItem href="/pools" dataTestId="pool-nav-link" isActive={isPoolActive}>
          <Trans>Pools</Trans>
        </MenuItem>
      </Box>
      <MenuItem dataTestId="farm-v3" href="/farms" isActive={isFarmsPage}>
        <Trans>Farms</Trans>
      </MenuItem>
      {isAstarEVMChain(connectedChainId) && (
        <>
          <Box display={{ sm: 'flex', lg: 'none', xxl: 'flex' }} width="full">
            <Row
              as="a"
              href="https://v2.arthswap.org/#/staking"
              className={styles.menuItem}
            >
              <Trans>Staking</Trans>
            </Row>
          </Box>
          <Box display={{ sm: 'flex', lg: 'none', xxl: 'flex' }} width="full">
            <Row
              as="a"
              href="https://v2.arthswap.org/#/idos"
              className={styles.menuItem}
            >
              <Trans>IDO</Trans>
            </Row>
          </Box>
        </>
      )}
      <Box marginY={{ sm: '4', md: 'unset' }}>
        <MenuDropdown />
      </Box>
    </>
  )
}

const Navbar = ({ blur }: { blur: boolean }) => {
  const isNftPage = useIsNftPage()
  const isDarkMode = useIsDarkMode()
  const sellPageState = useProfilePageState((state) => state.state)
  const navigate = useNavigate()

  return (
    <>
      {blur && <Blur />}
      <Nav>
        <Box display="flex" height="full" flexWrap="nowrap">
          <Box className={styles.leftSideContainer}>
            <Box className={styles.logoContainer}>
              <img src={isDarkMode ? 'images/arthswaplogo_dark.png' : 'images/arthswaplogo_light.png'} width="150" />
              {/*<UniIcon
                width='48'
                height='48'
                data-testid='uniswap-logo'
                className={styles.logo}
                onClick={() => {
                  navigate({
                    pathname: '/',
                    search: '?intro=true',
                  })
                }}
              />
              /*/}
            </Box>
            {!isNftPage && (
              <Box display={{ sm: 'flex', lg: 'none' }}>
                <ChainSelector leftAlign={true} />
              </Box>
            )}
            <Row display={{ sm: 'none', lg: 'flex' }}>
              <PageTabs />
            </Row>
          </Box>
          {/*
          #TODO Search bar functions are cool so we should enable this function later
          <Box className={styles.searchContainer}>
            <SearchBar />
          </Box>
            /*/}
          <Box className={styles.rightSideContainer}>
            <Row gap="12">
              {/*
              <Box position="relative" display={{ sm: 'flex', navSearchInputVisible: 'none' }}>
                <SearchBar />
              </Box>
              /*/}
              {isNftPage && sellPageState !== ProfilePageStateType.LISTING && <Bag />}
              {!isNftPage && (
                <Box display={{ sm: 'none', lg: 'flex' }}>
                  <ChainSelector />
                </Box>
              )}
              <Web3Status />
            </Row>
          </Box>
        </Box>
      </Nav>
    </>
  )
}

export default Navbar
